import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { MobileDateInput } from "@/components/ui/mobile-date-input";
import { toast } from "sonner";
import FilterBar, { getDefaultFilters } from "@/components/FilterBar";
import { Plus, Search, ClipboardList, CalendarIcon, Check, ChevronsUpDown, Eye, UserPlus, Users, ChevronRight, ChevronLeft, AlertCircle, AlertTriangle, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

import { API } from "@/config/api";
import { AREAS, REP_DIRECTORY, getRepDisplayName, VISIT_TYPES, APPOINTMENT_TYPES } from "@/config/constants";
import { RepField, isRepUser, repOf } from "@/components/RepField";
import LocationSelector from "@/components/LocationSelector";

const formatLocation = (area, subregion, suburb) => {
  const parts = [suburb, subregion, area].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "-";
};

const MARKETING_MATERIALS = ["Brochures", "Starter kit", "Referral Pad", "Business card", "SS ring case", "Promotional Gifts"];
const TITLES = ["Dr", "Prof", "Mr", "Mrs", "Ms"];

// Initial form states
const initialExistingDoctorForm = {
  rep_name: "",
  doctor_id: "",
  did_see_doctor: null,
  date_of_visit: "",
  visit_type: "",
  entry_type: "visit",
  city: "",
  area: "",
  suburb: "",
  marketing_material_used: [],  // Changed to array for multi-select
  gifts_left: "",  // Now "Marketing Material Comments"
  next_appointment: "",
  no_followup_scheduled: false,
  no_followup_reason: "",
  gatekeeper: "",
  comments: "",
  requests: "",
  source_appointment_id: ""  // For "Convert to Visit" flow
};

const initialNewDoctorForm = {
  rep_name: "",
  did_see_doctor: null,
  goodx_id: "",
  title: "",
  initials: "",
  doctor_name: "",
  surname: "",
  practice_name: "",
  practice_number: "",
  samdc: "",  // Legacy field - kept for backward compatibility
  mp_number: "",  // New required field
  qualification: "",
  city: "",
  area: "",
  suburb: "",
  medibase: "",
  doctor_phone: "",
  cell_number: "",
  doctor_email: "",
  address: "",
  postal_address: "",
  date_of_visit: "",
  visit_type: "",
  entry_type: "visit",
  marketing_material_used: [],  // Changed to array for multi-select
  gifts_left: "",  // Now "Marketing Material Comments"
  next_appointment: "",
  no_followup_scheduled: false,
  no_followup_reason: "",
  gatekeeper: "",
  comments: "",
  requests: ""
};

const VisitsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [user, setUser] = useState(null);
  
  // Location filter state
  const [filterCity, setFilterCity] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [filterSuburb, setFilterSuburb] = useState("");
  
  // Initialize filters once with lazy initialization
  const [filters, setFilters] = useState(() => getDefaultFilters());
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  
  // Form state
  const [step, setStep] = useState(1); // 1 = choose entry type, 2 = choose doctor type, 3 = fill form
  const [entryType, setEntryType] = useState("visit"); // "visit" or "appointment"
  const [doctorType, setDoctorType] = useState(null); // "existing" or "new"
  const [existingForm, setExistingForm] = useState(initialExistingDoctorForm);
  const [newDoctorForm, setNewDoctorForm] = useState(initialNewDoctorForm);
  const [submitting, setSubmitting] = useState(false);
  
  // Form validation
  const [formErrors, setFormErrors] = useState({});
  
  const validateVisitForm = (data) => {
    const errors = {};
    if (!data.visitDate) errors.visitDate = "Date is required";
    if (!data.rep_name) errors.rep_name = "Rep is required";
    if (!data.visit_type) errors.visit_type = "Visit Type is required";
    if (!data.selectedDoctor) errors.selectedDoctor = "Doctor Details are required";
    if (data.did_see_doctor === null || data.did_see_doctor === undefined) errors.did_see_doctor = "Did you see the doctor is required";
    return errors;
  };
  
  const validateAppointmentForm = (data) => {
    const errors = {};
    if (!data.appointment_date && !data.appointmentDate) errors.appointmentDate = "Date is required";
    if (!data.rep_name) errors.rep_name = "Rep is required";
    if (!data.appointment_type) errors.appointment_type = "Appointment Type is required";
    if (!data.selectedDoctor) errors.selectedDoctor = "Doctor Details are required";
    if (data.did_see_doctor === null || data.did_see_doctor === undefined) errors.did_see_doctor = "Did you see the doctor is required";
    return errors;
  };
  
  const clearFieldError = (field) => {
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };
  
  // Doctor search for existing
  const [doctorSearchOpen, setDoctorSearchOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  
  // Date pickers
  const [visitDate, setVisitDate] = useState(null);
  const [nextAppDate, setNextAppDate] = useState(null);

  // Appointment form state
  const [appointmentForm, setAppointmentForm] = useState({
    rep_name: "",
    appointment_type: "",
    did_see_doctor: false,
    marketing_material_used: [],
    marketing_material_comments: "",
    gatekeeper: "",
    comments: "",
    requests: ""
  });
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState("");
  const [scheduleAdvice, setScheduleAdvice] = useState(null);
  const [nextAppointmentDate, setNextAppointmentDate] = useState(null);
  const [nextAppointmentTime, setNextAppointmentTime] = useState("");

  // Next appointment time for visit forms (Change 2)
  const [nextAppTime, setNextAppTime] = useState("");

  // Conflict warning state
  const [conflictWarning, setConflictWarning] = useState(null);
  const [conflictChecking, setConflictChecking] = useState(false);

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error("Failed to fetch user");
      }
    };
    fetchUser();
  }, []);

  // Reps only ever log against themselves — keep every form's rep field on their own name
  const lockedRep = isRepUser(user) ? repOf(user) : null;
  useEffect(() => {
    if (!lockedRep) return;
    setExistingForm((prev) => (prev.rep_name === lockedRep ? prev : { ...prev, rep_name: lockedRep }));
    setNewDoctorForm((prev) => (prev.rep_name === lockedRep ? prev : { ...prev, rep_name: lockedRep }));
    setAppointmentForm((prev) => (prev.rep_name === lockedRep ? prev : { ...prev, rep_name: lockedRep }));
  }, [lockedRep, dialogOpen, step, doctorType]);

  // Handle URL params for "Convert to Visit" flow from calendar
  useEffect(() => {
    const doctorId = searchParams.get("doctor_id");
    const appointmentId = searchParams.get("appointment_id");
    const doctorName = searchParams.get("doctor_name");
    const practiceName = searchParams.get("practice_name");
    const visitType = searchParams.get("visit_type");
    const repName = searchParams.get("rep_name");  // Inherit from appointment
    const area = searchParams.get("area");
    
    if (doctorId || appointmentId) {
      // Pre-fill form and open dialog
      setDoctorType("existing");
      setEntryType(searchParams.get("entry_type") === "appointment" ? "appointment" : "visit");
      setStep(3);
      setDialogOpen(true);
      
      // If we have doctor_id, find and select the doctor
      if (doctorId) {
        // Try to find doctor in list
        const foundDoctor = doctors.find(d => d.doctor_id === doctorId);
        if (foundDoctor) {
          setSelectedDoctor(foundDoctor);
          setExistingForm(prev => ({
            ...prev,
            doctor_id: doctorId,
            source_appointment_id: appointmentId || "",
            visit_type: visitType || prev.visit_type,
            rep_name: repName || prev.rep_name,  // Inherit from appointment
            area: area || foundDoctor.area || prev.area
          }));
        } else {
          // Doctor not in list yet, set basic info
          setSelectedDoctor({
            doctor_id: doctorId,
            doctor_name: doctorName || "",
            practice_name: practiceName || "",
            area: area || ""
          });
          setExistingForm(prev => ({
            ...prev,
            doctor_id: doctorId,
            source_appointment_id: appointmentId || "",
            visit_type: visitType || prev.visit_type,
            rep_name: repName || prev.rep_name,  // Inherit from appointment
            area: area || prev.area
          }));
        }
      }
      
      // Set today's date as visit date
      setVisitDate(new Date());
    }
  }, [searchParams, doctors]);

  // Fetch visits with filters
  const fetchVisits = useCallback(async (currentFilters = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (currentFilters.rep && currentFilters.rep !== "all") params.append("rep", currentFilters.rep);
      if (currentFilters.territory && currentFilters.territory !== "all") params.append("area", currentFilters.territory);
      if (currentFilters.doctorId) params.append("doctor_id", currentFilters.doctorId);
      if (currentFilters.startDate) params.append("start_date", currentFilters.startDate);
      if (currentFilters.endDate) params.append("end_date", currentFilters.endDate);
      
      const response = await axios.get(`${API}/visits?${params.toString()}`);
      setVisits(response.data);
    } catch (error) {
      toast.error("Failed to fetch visits");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load only
  useEffect(() => {
    fetchVisits(filters);
    fetchDoctors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle single filter change (updates UI only, no fetch)
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Apply filters - triggers data fetch
  const handleApplyFilters = useCallback(() => {
    fetchVisits(filters);
  }, [filters, fetchVisits]);

  // Clear filters - reset to defaults and fetch
  const handleClearFilters = useCallback(() => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setFilterCity("");
    setFilterArea("");
    setFilterSuburb("");
    fetchVisits(defaults);
  }, [fetchVisits]);

  // Handle preset apply - set filters and fetch data
  const handlePresetApply = useCallback((presetFilters) => {
    setFilters(presetFilters);
    fetchVisits(presetFilters);
  }, [fetchVisits]);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      console.error("Failed to fetch doctors");
    }
  };

  const resetForm = () => {
    setStep(1);
    setEntryType("visit");
    setDoctorType(null);
    setExistingForm(initialExistingDoctorForm);
    setNewDoctorForm(initialNewDoctorForm);
    setSelectedDoctor(null);
    setVisitDate(null);
    setNextAppDate(null);
    setAppointmentForm({
      rep_name: "",
      appointment_type: "",
      did_see_doctor: false,
      marketing_material_used: [],
      marketing_material_comments: "",
      gatekeeper: "",
      comments: "",
      requests: ""
    });
    setAppointmentDate(null);
    setAppointmentTime("");
    setNextAppointmentDate(null);
    setNextAppointmentTime("");
    setNextAppTime("");
    setConflictWarning(null);
    setConflictChecking(false);
    setFormErrors({});
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  const checkAppointmentConflicts = async (date, time) => {
    if (!date || !time) return;
    const repName = user?.assigned_rep_name || user?.name;
    if (!repName) return;
    setConflictChecking(true);
    setConflictWarning(null);
    try {
      const response = await axios.post(`${API}/calendar/appointments/check-conflicts`, {
        rep_name: repName,
        appointment_date: date,
        appointment_time: time,
        duration_minutes: 60
      });
      if (response.data.has_conflicts) {
        setConflictWarning(response.data.conflicts);
      } else {
        setConflictWarning(null);
      }
    } catch {
      setConflictWarning(null);
    } finally {
      setConflictChecking(false);
    }
  };

  const handleSubmitExisting = async (e) => {
    e.preventDefault();
    
    // Unified validation
    const visitData = {
      visitDate,
      rep_name: existingForm.rep_name,
      visit_type: existingForm.visit_type,
      selectedDoctor,
      did_see_doctor: existingForm.did_see_doctor
    };
    const errors = validateVisitForm(visitData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Please fill in all required fields");
      return;
    }
    setFormErrors({});
    
    if (!existingForm.area) {
      toast.error("Please select an area");
      return;
    }
    if (!existingForm.marketing_material_used || existingForm.marketing_material_used.length === 0) {
      toast.error("Please select at least one marketing material");
      return;
    }
    
    // Validate follow-up requirement
    if (!existingForm.no_followup_scheduled && !nextAppDate) {
      toast.error("Follow-up date is required. If no follow-up is scheduled, please check 'No follow-up scheduled' and provide a reason.");
      return;
    }
    if (existingForm.no_followup_scheduled && !existingForm.no_followup_reason) {
      toast.error("Please provide a reason for not scheduling a follow-up");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...existingForm,
        rep_name: lockedRep || existingForm.rep_name,
        doctor_id: selectedDoctor.doctor_id,
        date_of_visit: format(visitDate, "yyyy-MM-dd"),
        entry_type: entryType,
        area: existingForm.area,
        marketing_material_used: existingForm.marketing_material_used,  // Array format
        next_appointment: nextAppDate && !existingForm.no_followup_scheduled ? format(nextAppDate, "yyyy-MM-dd") : "",
        next_appointment_time: nextAppTime || null,
        no_followup_scheduled: existingForm.no_followup_scheduled,
        no_followup_reason: existingForm.no_followup_reason,
        source_appointment_id: existingForm.source_appointment_id || ""
      };

      await axios.post(`${API}/visits`, payload);
      toast.success("Entry logged successfully");
      
      // Clear URL params if coming from calendar
      if (searchParams.get("doctor_id") || searchParams.get("appointment_id")) {
        window.history.replaceState({}, "", "/visits");
      }
      
      handleDialogClose();
      fetchVisits(filters);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log visit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitNewDoctor = async (e) => {
    e.preventDefault();
    if (!visitDate) {
      toast.error("Please select a visit date");
      return;
    }
    if (!newDoctorForm.doctor_name || !newDoctorForm.practice_name || !newDoctorForm.doctor_phone || !newDoctorForm.doctor_email || !newDoctorForm.area) {
      toast.error("Please fill all required fields");
      return;
    }
    if (newDoctorForm.did_see_doctor === null) {
      toast.error("Please indicate if you saw the doctor");
      return;
    }
    if (!newDoctorForm.visit_type) {
      toast.error("Please select a visit type");
      return;
    }
    if (!newDoctorForm.marketing_material_used || newDoctorForm.marketing_material_used.length === 0) {
      toast.error("Please select at least one marketing material");
      return;
    }
    
    // Validate follow-up requirement
    if (!newDoctorForm.no_followup_scheduled && !nextAppDate) {
      toast.error("Follow-up date is required. If no follow-up is scheduled, please check 'No follow-up scheduled' and provide a reason.");
      return;
    }
    if (newDoctorForm.no_followup_scheduled && !newDoctorForm.no_followup_reason) {
      toast.error("Please provide a reason for not scheduling a follow-up");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...newDoctorForm,
        rep_name: lockedRep || newDoctorForm.rep_name,
        date_of_visit: format(visitDate, "yyyy-MM-dd"),
        entry_type: entryType,
        marketing_material_used: newDoctorForm.marketing_material_used,  // Array format
        next_appointment: nextAppDate && !newDoctorForm.no_followup_scheduled ? format(nextAppDate, "yyyy-MM-dd") : "",
        next_appointment_time: nextAppTime || null,
        no_followup_scheduled: newDoctorForm.no_followup_scheduled,
        no_followup_reason: newDoctorForm.no_followup_reason
      };

      await axios.post(`${API}/visits/new-doctor`, payload);
      toast.success("Doctor added and entry logged successfully!");
      handleDialogClose();
      fetchVisits(filters);
      fetchDoctors(); // Refresh doctors list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add doctor and log visit");
    } finally {
      setSubmitting(false);
    }
  };


  // Travel-aware schedule check — warns, never blocks
  useEffect(() => {
    if (entryType !== "appointment" || !appointmentDate || !appointmentTime || !selectedDoctor) {
      setScheduleAdvice(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/rep/schedule-advice`, {
          rep_name: appointmentForm.rep_name || undefined,
          appointment_date: format(appointmentDate, "yyyy-MM-dd"),
          appointment_time: appointmentTime,
          duration_minutes: 60,
          doctor_name: selectedDoctor.doctor_name,
          area: selectedDoctor.area || selectedDoctor.city_territory || "",
          subregion: selectedDoctor.subregion || "",
        });
        setScheduleAdvice(res.data);
      } catch {
        setScheduleAdvice(null);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [entryType, appointmentDate, appointmentTime, selectedDoctor, appointmentForm.rep_name]);

  const handleSubmitAppointment = async (e) => {
    e.preventDefault();
    
    // Unified validation
    const appointmentData = {
      appointment_date: appointmentDate,
      appointmentDate: appointmentDate,
      rep_name: appointmentForm.rep_name,
      appointment_type: appointmentForm.appointment_type,
      selectedDoctor,
      did_see_doctor: appointmentForm.did_see_doctor
    };
    const errors = validateAppointmentForm(appointmentData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Please fill in all required fields");
      return;
    }
    setFormErrors({});
    
    if (!appointmentTime) {
      toast.error("Please select an appointment time");
      return;
    }
    if (!appointmentForm.marketing_material_used || appointmentForm.marketing_material_used.length === 0) {
      toast.error("Please select at least one marketing material");
      return;
    }

    setSubmitting(true);
    try {
      const dateStr = format(appointmentDate, "yyyy-MM-dd");
      const hasNextAppointment = nextAppointmentDate !== null;

      // Call 1: Log the visit entry
      const visitPayload = {
        rep_name: lockedRep || appointmentForm.rep_name,
        doctor_id: selectedDoctor.doctor_id,
        date_of_visit: dateStr,
        visit_type: appointmentForm.appointment_type,
        entry_type: "appointment",
        area: selectedDoctor.area || "",
        did_see_doctor: appointmentForm.did_see_doctor,
        marketing_material_used: appointmentForm.marketing_material_used,
        gifts_left: appointmentForm.marketing_material_comments,
        next_appointment: hasNextAppointment ? format(nextAppointmentDate, "yyyy-MM-dd") : "",
        no_followup_scheduled: !hasNextAppointment,
        no_followup_reason: !hasNextAppointment ? "Appointment entry - no follow-up scheduled" : "",
        gatekeeper: appointmentForm.gatekeeper,
        comments: appointmentForm.comments,
        requests: appointmentForm.requests,
      };

      await axios.post(`${API}/visits`, visitPayload);

      // Call 2: Create the calendar appointment
      const calendarPayload = {
        doctor_id: selectedDoctor.doctor_id,
        doctor_name: selectedDoctor.doctor_name,
        practice_name: selectedDoctor.practice_name || "",
        area: selectedDoctor.area || "",
        assigned_rep: lockedRep || appointmentForm.rep_name,
        appointment_date: dateStr,
        appointment_time: appointmentTime,
        visit_type: appointmentForm.appointment_type,
        notes: appointmentForm.comments || "",
        doctor_phone_snapshot: selectedDoctor.doctor_phone || "",
        doctor_address_snapshot: selectedDoctor.address || "",
      };

      await axios.post(`${API}/calendar/appointments`, calendarPayload);

      // Call 3 (optional): Create follow-up appointment
      if (hasNextAppointment) {
        const followUpPayload = {
          doctor_id: selectedDoctor.doctor_id,
          doctor_name: selectedDoctor.doctor_name,
          practice_name: selectedDoctor.practice_name || "",
          area: selectedDoctor.area || "",
          assigned_rep: lockedRep || appointmentForm.rep_name,
          appointment_date: format(nextAppointmentDate, "yyyy-MM-dd"),
          appointment_time: nextAppointmentTime || null,
          visit_type: "Follow-up",
          notes: `Follow-up from ${appointmentForm.appointment_type} on ${dateStr}`,
          doctor_phone_snapshot: selectedDoctor.doctor_phone || "",
          doctor_address_snapshot: selectedDoctor.address || "",
        };

        await axios.post(`${API}/calendar/appointments`, followUpPayload);
      }

      toast.success("Appointment logged and added to calendar");
      handleDialogClose();
      fetchVisits(filters);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log appointment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewVisit = (visit) => {
    setSelectedVisit(visit);
    setViewDialogOpen(true);
  };

  const filteredVisits = visits.filter((visit) => {
    // Location filter (cascading: suburb > area > city)
    if (filterSuburb) {
      if (visit.suburb !== filterSuburb) return false;
    } else if (filterArea) {
      if (visit.area !== filterArea) return false;
    } else if (filterCity) {
      if (visit.city !== filterCity) return false;
    }
    // Text search filter
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      visit.doctor_name?.toLowerCase().includes(searchLower) ||
      visit.practice_name?.toLowerCase().includes(searchLower) ||
      visit.rep_name?.toLowerCase().includes(searchLower)
    );
  });

  // Step 1: Choose entry type (Visit vs Appointment)
  const renderStep1 = () => (
    <div className="space-y-6 py-4">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          What type of entry is this?
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose between a visit or an appointment
        </p>
      </div>

      <RadioGroup
        value={entryType}
        onValueChange={setEntryType}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <label
          htmlFor="entry-visit"
          className={cn(
            "flex flex-col items-center justify-center p-6 rounded-xl border-2 cursor-pointer transition-all",
            entryType === "visit"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="visit" id="entry-visit" className="sr-only" />
          <ClipboardList className="w-12 h-12 text-primary mb-3" />
          <span className="font-semibold text-lg">Visit</span>
          <span className="text-sm text-muted-foreground text-center mt-1">
            Drop Off, Spec Call, Detailed Call, etc.
          </span>
        </label>

        <label
          htmlFor="entry-appointment"
          className={cn(
            "flex flex-col items-center justify-center p-6 rounded-xl border-2 cursor-pointer transition-all",
            entryType === "appointment"
              ? "border-blue-600 bg-blue-50"
              : "border-border hover:border-blue-400/50"
          )}
        >
          <RadioGroupItem value="appointment" id="entry-appointment" className="sr-only" />
          <CalendarIcon className="w-12 h-12 text-blue-600 mb-3" />
          <span className="font-semibold text-lg">Appointment</span>
          <span className="text-sm text-muted-foreground text-center mt-1">
            Lunch App, F2F, Coffee, Breakfast
          </span>
        </label>
      </RadioGroup>

      <div className="flex justify-end pt-4">
        <Button
          type="button"
          onClick={() => setStep(2)}
          className="gap-2"
          data-testid="continue-to-doctor-type-button"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Step 2: Choose doctor type (Existing vs New)
  const renderStep2 = () => (
    <div className="space-y-6 py-4">
      <div className="flex items-center gap-2 mb-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setStep(1)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Badge variant={entryType === "appointment" ? "default" : "secondary"} className={entryType === "appointment" ? "bg-blue-100 text-blue-800" : ""}>
          {entryType === "appointment" ? "Appointment" : "Visit"}
        </Badge>
      </div>
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Is this an existing doctor or a new doctor?
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose the appropriate option to continue
        </p>
      </div>

      <RadioGroup
        value={doctorType}
        onValueChange={setDoctorType}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <label
          htmlFor="existing"
          className={cn(
            "flex flex-col items-center justify-center p-6 rounded-xl border-2 cursor-pointer transition-all",
            doctorType === "existing"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="existing" id="existing" className="sr-only" />
          <Users className="w-12 h-12 text-primary mb-3" />
          <span className="font-semibold text-lg">Existing Doctor</span>
          <span className="text-sm text-muted-foreground text-center mt-1">
            Quick entry log with doctor lookup
          </span>
        </label>

        <label
          htmlFor="new"
          className={cn(
            "flex flex-col items-center justify-center p-6 rounded-xl border-2 cursor-pointer transition-all",
            doctorType === "new"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="new" id="new" className="sr-only" />
          <UserPlus className="w-12 h-12 text-emerald-600 mb-3" />
          <span className="font-semibold text-lg">New Doctor</span>
          <span className="text-sm text-muted-foreground text-center mt-1">
            {isRepUser(user)
              ? "Capture the doctor on the Doctors page, then come back here"
              : "Full intake form + entry log"}
          </span>
        </label>
      </RadioGroup>

      <div className="flex justify-end pt-4">
        <Button
          type="button"
          onClick={() => {
            // Reps capture a new doctor on the Doctors page, then come straight back here
            if (doctorType === "new" && isRepUser(user)) {
              const returnTo = `/visits?entry_type=${entryType}`;
              setDialogOpen(false);
              navigate(`/doctors?returnTo=${encodeURIComponent(returnTo)}&context=visit`);
              return;
            }
            if (entryType === "appointment" && user?.name && REP_DIRECTORY.includes(user.name)) {
              setAppointmentForm(prev => ({ ...prev, rep_name: prev.rep_name || user.name }));
            }
            setStep(3);
          }}
          disabled={!doctorType}
          className="gap-2"
          data-testid="continue-to-form-button"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Step 3A: Existing Doctor Form
  const renderExistingDoctorForm = () => {
    const typeOptions = entryType === "appointment" ? APPOINTMENT_TYPES : VISIT_TYPES;
    const typeLabel = entryType === "appointment" ? "Appointment Type" : "Visit Type";
    return (
    <form onSubmit={handleSubmitExisting} className="space-y-4" data-testid="existing-doctor-form">
      <div className="flex items-center gap-2 mb-4">
        <Button type="button" variant="ghost" size="sm" onClick={() => setStep(2)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Badge variant={entryType === "appointment" ? "default" : "secondary"} className={entryType === "appointment" ? "bg-blue-100 text-blue-800" : ""}>
          {entryType === "appointment" ? "Appointment" : "Visit"}
        </Badge>
        <Badge variant="secondary">Existing Doctor</Badge>
      </div>

      {/* Rep Name */}
      <div>
        <Label htmlFor="rep_name">Rep Name *</Label>
        <RepField
          user={user}
          value={existingForm.rep_name}
          onChange={(value) => { setExistingForm({ ...existingForm, rep_name: value }); clearFieldError("rep_name"); }}
          hasError={Boolean(formErrors.rep_name)}
          testId="select-existing-rep"
        />
        {formErrors.rep_name && <p className="text-xs text-red-500 mt-1">{formErrors.rep_name}</p>}
      </div>

      {/* Doctor Selection */}
      <div>
        <Label>Doctor *</Label>
        <Popover open={doctorSearchOpen} onOpenChange={setDoctorSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={doctorSearchOpen}
              className={`w-full justify-between ${formErrors.selectedDoctor ? "border-red-500" : ""}`}
              data-testid="select-existing-doctor"
              onClick={() => clearFieldError("selectedDoctor")}
            >
              {selectedDoctor
                ? `${selectedDoctor.doctor_name} - ${selectedDoctor.practice_name}`
                : "Search and select doctor..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder="Search doctors..." />
              <CommandList>
                <CommandEmpty>No doctors found.</CommandEmpty>
                <CommandGroup>
                  {doctors.map((doctor) => (
                    <CommandItem
                      key={doctor.doctor_id}
                      value={`${doctor.doctor_name} ${doctor.practice_name}`}
                      onSelect={() => {
                        setSelectedDoctor(doctor);
                        // Auto-fill area from doctor if available
                        if (doctor.area) {
                          setExistingForm(prev => ({ ...prev, area: doctor.area }));
                        }
                        setDoctorSearchOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedDoctor?.doctor_id === doctor.doctor_id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div>
                        <p className="font-medium">{doctor.doctor_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {doctor.practice_name} • {doctor.area || doctor.city_territory || "No area"}
                        </p>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {selectedDoctor && (
          <div className="mt-2 p-3 bg-muted rounded-lg text-sm">
            <p><strong>ID:</strong> {selectedDoctor.doctor_id}</p>
            <p><strong>Practice:</strong> {selectedDoctor.practice_name}</p>
            <p><strong>Area:</strong> {selectedDoctor.area}</p>
            <p><strong>Phone:</strong> {selectedDoctor.doctor_phone}</p>
            <p><strong>Email:</strong> {selectedDoctor.doctor_email}</p>
          </div>
        )}
        {formErrors.selectedDoctor && <p className="text-xs text-red-500 mt-1">{formErrors.selectedDoctor}</p>}
      </div>

      {/* Did you see the doctor? */}
      <div>
        <Label>Did you see the doctor? *</Label>
        <div className={`flex gap-3 mt-2 ${formErrors.did_see_doctor ? "border-2 border-red-500 rounded p-2" : ""}`}>
          <button
            type="button"
            onClick={() => { setExistingForm({...existingForm, did_see_doctor: true}); clearFieldError("did_see_doctor"); }}
            className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
              existingForm.did_see_doctor === true 
                ? 'border-primary bg-primary text-white' 
                : 'border-gray-200 bg-white text-gray-700 hover:border-primary/50'
            }`}
            data-testid="btn-saw-doctor-yes"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => { setExistingForm({...existingForm, did_see_doctor: false}); clearFieldError("did_see_doctor"); }}
            className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
              existingForm.did_see_doctor === false 
                ? 'border-red-500 bg-red-500 text-white' 
                : 'border-gray-200 bg-white text-gray-700 hover:border-red-300'
            }`}
            data-testid="btn-saw-doctor-no"
          >
            No
          </button>
        </div>
        {formErrors.did_see_doctor && <p className="text-xs text-red-500 mt-1">{formErrors.did_see_doctor}</p>}
      </div>

      {/* Date of Visit */}
      <div>
        <Label>Date of Visit *</Label>
        <MobileDateInput
          value={visitDate}
          onChange={(d) => { setVisitDate(d); clearFieldError("visitDate"); }}
          placeholder="Select date"
          data-testid="select-existing-visit-date"
        />
        {formErrors.visitDate && <p className="text-xs text-red-500 mt-1">{formErrors.visitDate}</p>}
      </div>

      {/* Visit/Appointment Type */}
      <div>
        <Label>{typeLabel} *</Label>
        <Select
          value={existingForm.visit_type}
          onValueChange={(value) => { setExistingForm({ ...existingForm, visit_type: value }); clearFieldError("visit_type"); }}
          required
        >
          <SelectTrigger className={`h-11 sm:h-10 ${formErrors.visit_type ? "border-red-500" : ""}`} data-testid="select-existing-visit-type">
            <SelectValue placeholder={`Select ${typeLabel.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {typeOptions.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {formErrors.visit_type && <p className="text-xs text-red-500 mt-1">{formErrors.visit_type}</p>}
      </div>

      {/* Location */}
      <LocationSelector
        city={existingForm.city || ""}
        area={existingForm.area || ""}
        suburb={existingForm.suburb || ""}
        onLocationChange={(loc) => setExistingForm({ ...existingForm, city: loc.city, area: loc.area, suburb: loc.suburb })}
        required
      />

      {/* Marketing Material Used - Multi-select */}
      <div>
        <Label className="flex items-center gap-1">
          Marketing Material Used *
          <span className="text-xs text-muted-foreground">(select all that apply)</span>
        </Label>
        <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-md bg-gray-50">
          {MARKETING_MATERIALS.map((material) => (
            <div key={material} className="flex items-center space-x-2">
              <Checkbox
                id={`existing-material-${material}`}
                checked={existingForm.marketing_material_used.includes(material)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setExistingForm({ ...existingForm, marketing_material_used: [...existingForm.marketing_material_used, material] });
                  } else {
                    setExistingForm({ ...existingForm, marketing_material_used: existingForm.marketing_material_used.filter(m => m !== material) });
                  }
                }}
                data-testid={`checkbox-existing-material-${material.replace(/\s+/g, '-').toLowerCase()}`}
              />
              <Label htmlFor={`existing-material-${material}`} className="text-sm cursor-pointer">
                {material}
              </Label>
            </div>
          ))}
        </div>
        {existingForm.marketing_material_used.length === 0 && (
          <p className="text-xs text-orange-600 mt-1">Please select at least one material</p>
        )}
      </div>

      {/* Marketing Material Comments (formerly Gifts Left) */}
      <div>
        <Label htmlFor="gifts_left">Marketing Material Comments</Label>
        <Textarea
          id="gifts_left"
          value={existingForm.gifts_left}
          onChange={(e) => setExistingForm({ ...existingForm, gifts_left: e.target.value })}
          placeholder="Optional notes about materials left or special requests..."
          rows={2}
        />
      </div>

      {/* Next Appointment / Follow-up Section */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-4 h-4 text-primary" />
          <Label className="font-semibold">Follow-up Appointment *</Label>
        </div>
        
        {!existingForm.no_followup_scheduled && (
          <div className="mb-3">
            <Label>Next Appointment Date *</Label>
            <MobileDateInput
              value={nextAppDate}
              onChange={(d) => {
                setNextAppDate(d);
                if (d && nextAppTime) checkAppointmentConflicts(format(d, "yyyy-MM-dd"), nextAppTime);
                else { setConflictWarning(null); setConflictChecking(false); }
              }}
              placeholder="Select follow-up date"
              data-testid="select-existing-next-appointment"
            />
            <div className="mt-2">
              <Label>Appointment Time (optional)</Label>
              <input
                type="time"
                value={nextAppTime}
                onChange={(e) => {
                  const newTime = e.target.value;
                  setNextAppTime(newTime);
                  const date = nextAppDate ? format(nextAppDate, "yyyy-MM-dd") : null;
                  if (date && newTime) checkAppointmentConflicts(date, newTime);
                }}
                className="w-full h-10 px-3 border rounded-md text-sm mt-1"
                data-testid="input-next-app-time"
              />
              <p className="text-xs text-muted-foreground mt-1">
                If set, this time will appear on the rep&apos;s calendar
              </p>
              {conflictChecking && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" />
                  Checking your calendar...
                </p>
              )}
              {conflictWarning && conflictWarning.length > 0 && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">
                        Schedule conflict — {conflictWarning.length} existing appointment{conflictWarning.length > 1 ? "s" : ""} within 1 hour
                      </p>
                      <div className="mt-2 space-y-2">
                        {conflictWarning.map((conflict, i) => (
                          <div key={i} className="text-xs text-amber-700 bg-amber-100 rounded p-2">
                            <span className="font-medium">{conflict.doctor_name}</span>
                            <span className="mx-1">·</span>
                            <span>{conflict.area}</span>
                            <span className="mx-1">·</span>
                            <span>{conflict.appointment_time}</span>
                            <span className="mx-1">·</span>
                            <span>{conflict.visit_type}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-amber-600 mt-2">You can still save — this is a warning only.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
          <Checkbox
            id="no_followup_scheduled"
            checked={existingForm.no_followup_scheduled}
            onCheckedChange={(checked) => {
              setExistingForm({ ...existingForm, no_followup_scheduled: checked, no_followup_reason: checked ? existingForm.no_followup_reason : "" });
              if (checked) { setNextAppDate(null); setNextAppTime(""); setConflictWarning(null); setConflictChecking(false); }
            }}
            data-testid="checkbox-no-followup"
          />
          <div className="space-y-1">
            <Label htmlFor="no_followup_scheduled" className="text-sm font-medium cursor-pointer flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-600" />
              No follow-up scheduled
            </Label>
            <p className="text-xs text-muted-foreground">
              Check this if no follow-up is required for this visit
            </p>
          </div>
        </div>
        
        {existingForm.no_followup_scheduled && (
          <div className="mt-3">
            <Label htmlFor="no_followup_reason" className="flex items-center gap-1">
              Reason for No Follow-up *
              <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="no_followup_reason"
              value={existingForm.no_followup_reason}
              onChange={(e) => setExistingForm({ ...existingForm, no_followup_reason: e.target.value })}
              placeholder="e.g., Doctor not interested, Practice closed, One-time drop-off..."
              rows={2}
              className="mt-1"
              required
              data-testid="input-no-followup-reason"
            />
          </div>
        )}
      </div>

      {/* Gatekeeper */}
      <div>
        <Label htmlFor="gatekeeper">Gatekeeper</Label>
        <Input
          id="gatekeeper"
          value={existingForm.gatekeeper}
          onChange={(e) => setExistingForm({ ...existingForm, gatekeeper: e.target.value })}
          placeholder="Name of receptionist/secretary"
          className="h-11 sm:h-10"
        />
      </div>

      {/* Comments */}
      <div>
        <Label htmlFor="comments">Comments</Label>
        <Textarea
          id="comments"
          value={existingForm.comments}
          onChange={(e) => setExistingForm({ ...existingForm, comments: e.target.value })}
          placeholder="Notes about the visit..."
          rows={3}
        />
      </div>

      {/* Requests */}
      <div>
        <Label htmlFor="requests">Requests</Label>
        <Textarea
          id="requests"
          value={existingForm.requests}
          onChange={(e) => setExistingForm({ ...existingForm, requests: e.target.value })}
          placeholder="Any requests from the doctor..."
          rows={2}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={handleDialogClose}>Cancel</Button>
        <Button type="submit" disabled={submitting} data-testid="submit-existing-visit">
          {submitting ? "Saving..." : `Log ${entryType === "appointment" ? "Appointment" : "Visit"}`}
        </Button>
      </div>
    </form>
    );
  };

  // Step 3B: New Doctor Form
  const renderNewDoctorForm = () => {
    const typeOptions = entryType === "appointment" ? APPOINTMENT_TYPES : VISIT_TYPES;
    const typeLabel = entryType === "appointment" ? "Appointment Type" : "Visit Type";
    return (
    <form onSubmit={handleSubmitNewDoctor} className="space-y-4" data-testid="new-doctor-form">
      <div className="flex items-center gap-2 mb-4">
        <Button type="button" variant="ghost" size="sm" onClick={() => setStep(2)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Badge variant={entryType === "appointment" ? "default" : "secondary"} className={entryType === "appointment" ? "bg-blue-100 text-blue-800" : ""}>
          {entryType === "appointment" ? "Appointment" : "Visit"}
        </Badge>
        <Badge className="bg-emerald-100 text-emerald-800">New Doctor Intake</Badge>
      </div>

      {/* Rep Name */}
      <div>
        <Label htmlFor="rep_name">Rep Name *</Label>
        <RepField
          user={user}
          value={newDoctorForm.rep_name}
          onChange={(value) => setNewDoctorForm({ ...newDoctorForm, rep_name: value })}
          testId="select-new-rep"
        />
      </div>

      {/* Did you see the doctor? */}
      <div>
        <Label>Did you see the doctor? *</Label>
        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={() => setNewDoctorForm({...newDoctorForm, did_see_doctor: true})}
            className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
              newDoctorForm.did_see_doctor === true 
                ? 'border-primary bg-primary text-white' 
                : 'border-gray-200 bg-white text-gray-700 hover:border-primary/50'
            }`}
            data-testid="btn-new-saw-doctor-yes"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setNewDoctorForm({...newDoctorForm, did_see_doctor: false})}
            className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
              newDoctorForm.did_see_doctor === false 
                ? 'border-red-500 bg-red-500 text-white' 
                : 'border-gray-200 bg-white text-gray-700 hover:border-red-300'
            }`}
            data-testid="btn-new-saw-doctor-no"
          >
            No
          </button>
        </div>
      </div>

      {/* Section: Doctor Demographics */}
      <div className="border-t pt-4 mt-4">
        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-4">
          Doctor Information
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="goodx_id">GoodX ID</Label>
            <Input
              id="goodx_id"
              value={newDoctorForm.goodx_id}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, goodx_id: e.target.value })}
              placeholder="GoodX ID"
            />
          </div>
          <div>
            <Label htmlFor="title">Title</Label>
            <Select
              value={newDoctorForm.title}
              onValueChange={(value) => setNewDoctorForm({ ...newDoctorForm, title: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select title" />
              </SelectTrigger>
              <SelectContent>
                {TITLES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <Label htmlFor="initials">Initials</Label>
            <Input
              id="initials"
              value={newDoctorForm.initials}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, initials: e.target.value })}
              placeholder="e.g., JM"
            />
          </div>
          <div>
            <Label htmlFor="doctor_name">Doctor Name *</Label>
            <Input
              id="doctor_name"
              value={newDoctorForm.doctor_name}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, doctor_name: e.target.value })}
              required
              placeholder="First name"
              data-testid="input-new-doctor-name"
            />
          </div>
          <div>
            <Label htmlFor="surname">Surname</Label>
            <Input
              id="surname"
              value={newDoctorForm.surname}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, surname: e.target.value })}
              placeholder="Last name"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <Label htmlFor="practice_name">Practice Name *</Label>
            <Input
              id="practice_name"
              value={newDoctorForm.practice_name}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, practice_name: e.target.value })}
              required
              placeholder="Practice name"
              data-testid="input-new-practice-name"
            />
          </div>
          <div>
            <Label htmlFor="practice_number">Practice Number</Label>
            <Input
              id="practice_number"
              value={newDoctorForm.practice_number}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, practice_number: e.target.value })}
              placeholder="Practice #"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <Label htmlFor="mp_number">MP Number *</Label>
            <Input
              id="mp_number"
              value={newDoctorForm.mp_number}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, mp_number: e.target.value })}
              placeholder="Required"
              required
            />
          </div>
          <div>
            <Label htmlFor="qualification">Qualification</Label>
            <Input
              id="qualification"
              value={newDoctorForm.qualification}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, qualification: e.target.value })}
              placeholder="e.g., MBChB, FCFP"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <LocationSelector
              city={newDoctorForm.city || ""}
              area={newDoctorForm.area || ""}
              suburb={newDoctorForm.suburb || ""}
              onLocationChange={(loc) => setNewDoctorForm({ ...newDoctorForm, city: loc.city, area: loc.area, suburb: loc.suburb })}
              required
            />
          </div>
          <div>
            <Label htmlFor="medibase">Medibase</Label>
            <Input
              id="medibase"
              value={newDoctorForm.medibase}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, medibase: e.target.value })}
              placeholder="Medibase ID"
            />
          </div>
        </div>
      </div>

      {/* Section: Contact Information */}
      <div className="border-t pt-4 mt-4">
        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-4">
          Contact Information
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="doctor_phone">Phone *</Label>
            <Input
              id="doctor_phone"
              type="tel"
              value={newDoctorForm.doctor_phone}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, doctor_phone: e.target.value })}
              required
              placeholder="Office phone"
              data-testid="input-new-doctor-phone"
            />
          </div>
          <div>
            <Label htmlFor="cell_number">Cell #</Label>
            <Input
              id="cell_number"
              type="tel"
              value={newDoctorForm.cell_number}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, cell_number: e.target.value })}
              placeholder="Mobile number"
            />
          </div>
        </div>

        <div className="mt-4">
          <Label htmlFor="doctor_email">Email *</Label>
          <Input
            id="doctor_email"
            type="email"
            value={newDoctorForm.doctor_email}
            onChange={(e) => setNewDoctorForm({ ...newDoctorForm, doctor_email: e.target.value })}
            required
            placeholder="doctor@example.com"
            data-testid="input-new-doctor-email"
          />
        </div>

        <div className="mt-4">
          <Label htmlFor="address">Address</Label>
          <Textarea
            id="address"
            value={newDoctorForm.address}
            onChange={(e) => setNewDoctorForm({ ...newDoctorForm, address: e.target.value })}
            placeholder="Physical address"
            rows={2}
          />
        </div>

        <div className="mt-4">
          <Label htmlFor="postal_address">Postal Address</Label>
          <Textarea
            id="postal_address"
            value={newDoctorForm.postal_address}
            onChange={(e) => setNewDoctorForm({ ...newDoctorForm, postal_address: e.target.value })}
            placeholder="Postal address"
            rows={2}
          />
        </div>
      </div>

      {/* Section: Visit Information */}
      <div className="border-t pt-4 mt-4">
        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-4">
          Visit Information
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Date of Visit *</Label>
            <MobileDateInput
              value={visitDate}
              onChange={setVisitDate}
              placeholder="Select date"
              data-testid="select-new-visit-date"
            />
          </div>
          <div>
            <Label>{typeLabel} *</Label>
            <Select
              value={newDoctorForm.visit_type}
              onValueChange={(value) => setNewDoctorForm({ ...newDoctorForm, visit_type: value })}
              required
            >
              <SelectTrigger data-testid="select-new-visit-type">
                <SelectValue placeholder={`Select ${typeLabel.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Marketing Material Used - Multi-select */}
        <div className="mt-4">
          <Label className="flex items-center gap-1">
            Marketing Material Used *
            <span className="text-xs text-muted-foreground">(select all that apply)</span>
          </Label>
          <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-md bg-gray-50">
            {MARKETING_MATERIALS.map((material) => (
              <div key={material} className="flex items-center space-x-2">
                <Checkbox
                  id={`new-material-${material}`}
                  checked={newDoctorForm.marketing_material_used.includes(material)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setNewDoctorForm({ ...newDoctorForm, marketing_material_used: [...newDoctorForm.marketing_material_used, material] });
                    } else {
                      setNewDoctorForm({ ...newDoctorForm, marketing_material_used: newDoctorForm.marketing_material_used.filter(m => m !== material) });
                    }
                  }}
                  data-testid={`checkbox-new-material-${material.replace(/\s+/g, '-').toLowerCase()}`}
                />
                <Label htmlFor={`new-material-${material}`} className="text-sm cursor-pointer">
                  {material}
                </Label>
              </div>
            ))}
          </div>
          {newDoctorForm.marketing_material_used.length === 0 && (
            <p className="text-xs text-orange-600 mt-1">Please select at least one material</p>
          )}
        </div>

        {/* Marketing Material Comments (formerly Gifts Left) */}
        <div className="mt-4">
          <Label htmlFor="new_gifts">Marketing Material Comments</Label>
          <Textarea
            id="new_gifts"
            value={newDoctorForm.gifts_left}
            onChange={(e) => setNewDoctorForm({ ...newDoctorForm, gifts_left: e.target.value })}
            placeholder="Optional notes about materials left or special requests..."
            rows={2}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <Label htmlFor="new_gatekeeper">Gatekeeper</Label>
            <Input
              id="new_gatekeeper"
              value={newDoctorForm.gatekeeper}
              onChange={(e) => setNewDoctorForm({ ...newDoctorForm, gatekeeper: e.target.value })}
              placeholder="Receptionist name"
              className="h-11 sm:h-10"
            />
          </div>
        </div>
        
        {/* Next Appointment / Follow-up Section */}
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-4 h-4 text-primary" />
            <Label className="font-semibold">Follow-up Appointment *</Label>
          </div>
          
          {!newDoctorForm.no_followup_scheduled && (
            <div className="mb-3">
              <Label>Next Appointment Date *</Label>
              <MobileDateInput
                value={nextAppDate}
                onChange={(d) => {
                  setNextAppDate(d);
                  if (d && nextAppTime) checkAppointmentConflicts(format(d, "yyyy-MM-dd"), nextAppTime);
                  else { setConflictWarning(null); setConflictChecking(false); }
                }}
                placeholder="Select follow-up date"
                data-testid="select-new-next-appointment"
              />
              <div className="mt-2">
                <Label>Appointment Time (optional)</Label>
                <input
                  type="time"
                  value={nextAppTime}
                  onChange={(e) => {
                    const newTime = e.target.value;
                    setNextAppTime(newTime);
                    const date = nextAppDate ? format(nextAppDate, "yyyy-MM-dd") : null;
                    if (date && newTime) checkAppointmentConflicts(date, newTime);
                  }}
                  className="w-full h-10 px-3 border rounded-md text-sm mt-1"
                  data-testid="input-new-next-app-time"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  If set, this time will appear on the rep&apos;s calendar
                </p>
                {conflictChecking && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" />
                    Checking your calendar...
                  </p>
                )}
                {conflictWarning && conflictWarning.length > 0 && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-800">
                          Schedule conflict — {conflictWarning.length} existing appointment{conflictWarning.length > 1 ? "s" : ""} within 1 hour
                        </p>
                        <div className="mt-2 space-y-2">
                          {conflictWarning.map((conflict, i) => (
                            <div key={i} className="text-xs text-amber-700 bg-amber-100 rounded p-2">
                              <span className="font-medium">{conflict.doctor_name}</span>
                              <span className="mx-1">·</span>
                              <span>{conflict.area}</span>
                              <span className="mx-1">·</span>
                              <span>{conflict.appointment_time}</span>
                              <span className="mx-1">·</span>
                              <span>{conflict.visit_type}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-amber-600 mt-2">You can still save — this is a warning only.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
            <Checkbox
              id="new_no_followup_scheduled"
              checked={newDoctorForm.no_followup_scheduled}
              onCheckedChange={(checked) => {
                setNewDoctorForm({ ...newDoctorForm, no_followup_scheduled: checked, no_followup_reason: checked ? newDoctorForm.no_followup_reason : "" });
                if (checked) { setNextAppDate(null); setNextAppTime(""); setConflictWarning(null); setConflictChecking(false); }
              }}
              data-testid="checkbox-new-no-followup"
            />
            <div className="space-y-1">
              <Label htmlFor="new_no_followup_scheduled" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                No follow-up scheduled
              </Label>
              <p className="text-xs text-muted-foreground">
                Check this if no follow-up is required for this visit
              </p>
            </div>
          </div>
          
          {newDoctorForm.no_followup_scheduled && (
            <div className="mt-3">
              <Label htmlFor="new_no_followup_reason" className="flex items-center gap-1">
                Reason for No Follow-up *
                <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="new_no_followup_reason"
                value={newDoctorForm.no_followup_reason}
                onChange={(e) => setNewDoctorForm({ ...newDoctorForm, no_followup_reason: e.target.value })}
                placeholder="e.g., Doctor not interested, Practice closed, One-time drop-off..."
                rows={2}
                className="mt-1"
                required
                data-testid="input-new-no-followup-reason"
              />
            </div>
          )}
        </div>

        <div className="mt-4">
          <Label htmlFor="new_comments">Comments</Label>
          <Textarea
            id="new_comments"
            value={newDoctorForm.comments}
            onChange={(e) => setNewDoctorForm({ ...newDoctorForm, comments: e.target.value })}
            placeholder="Notes about the visit..."
            rows={3}
          />
        </div>

        <div className="mt-4">
          <Label htmlFor="new_requests">Requests</Label>
          <Textarea
            id="new_requests"
            value={newDoctorForm.requests}
            onChange={(e) => setNewDoctorForm({ ...newDoctorForm, requests: e.target.value })}
            placeholder="Any requests from the doctor..."
            rows={2}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={handleDialogClose}>Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-new-doctor-visit">
          {submitting ? "Saving..." : `Add Doctor & Log ${entryType === "appointment" ? "Appointment" : "Visit"}`}
        </Button>
      </div>
    </form>
    );
  };

  // Step 3C: Appointment Form (existing doctor + appointment)
  const renderAppointmentForm = () => (
    <form onSubmit={handleSubmitAppointment} className="space-y-4" data-testid="appointment-form">
      <div className="flex items-center gap-2 mb-4">
        <Button type="button" variant="ghost" size="sm" onClick={() => setStep(2)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Badge className="bg-blue-100 text-blue-800">Appointment</Badge>
        <Badge variant="secondary">Existing Doctor</Badge>
      </div>

      {/* 1. Appointment Date */}
      <div>
        <Label>Appointment Date *</Label>
        <MobileDateInput
          value={appointmentDate}
          onChange={(d) => { setAppointmentDate(d); clearFieldError("appointmentDate"); }}
          placeholder="Select date"
          data-testid="select-appointment-date"
        />
        {formErrors.appointmentDate && <p className="text-xs text-red-500 mt-1">{formErrors.appointmentDate}</p>}
      </div>

      {/* 2. Appointment Time */}
      <div>
        <Label htmlFor="appointment_time">Appointment Time *</Label>
        <input
          type="time"
          id="appointment_time"
          value={appointmentTime}
          onChange={(e) => setAppointmentTime(e.target.value)}
          required
          className="flex h-11 sm:h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          data-testid="input-appointment-time"
        />
        {scheduleAdvice?.has_warnings && (
          <div className="mt-2 space-y-2" data-testid="schedule-advice">
            {scheduleAdvice.warnings.slice(0, 3).map((warning, index) => (
              <div
                key={index}
                className={cn(
                  "flex gap-2 rounded-md border px-3 py-2",
                  warning.severity === "high"
                    ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/40"
                    : "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40"
                )}
                data-testid={`schedule-warning-${warning.kind}`}
              >
                <AlertTriangle
                  className={cn(
                    "w-4 h-4 shrink-0 mt-0.5",
                    warning.severity === "high" ? "text-red-600" : "text-amber-600"
                  )}
                />
                <div className="text-xs">
                  <p className="font-semibold">
                    {warning.severity === "high" ? "Tight squeeze" : "Heads up"}
                  </p>
                  <p className="mt-0.5">{warning.message}</p>
                </div>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">
              You can still book this — it is only a warning.
            </p>
          </div>
        )}
      </div>

      {/* 3. Rep */}
      <div>
        <Label htmlFor="appt_rep_name">Rep *</Label>
        <RepField
          user={user}
          value={appointmentForm.rep_name}
          onChange={(value) => { setAppointmentForm({ ...appointmentForm, rep_name: value }); clearFieldError("rep_name"); }}
          hasError={Boolean(formErrors.rep_name)}
          testId="select-appointment-rep"
        />
        {formErrors.rep_name && <p className="text-xs text-red-500 mt-1">{formErrors.rep_name}</p>}
      </div>

      {/* 4. Appointment Type */}
      <div>
        <Label>Appointment Type *</Label>
        <Select
          value={appointmentForm.appointment_type}
          onValueChange={(value) => { setAppointmentForm({ ...appointmentForm, appointment_type: value }); clearFieldError("appointment_type"); }}
          required
        >
          <SelectTrigger className={`h-11 sm:h-10 ${formErrors.appointment_type ? "border-red-500" : ""}`} data-testid="select-appointment-type">
            <SelectValue placeholder="Select appointment type" />
          </SelectTrigger>
          <SelectContent>
            {APPOINTMENT_TYPES.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {formErrors.appointment_type && <p className="text-xs text-red-500 mt-1">{formErrors.appointment_type}</p>}
      </div>

      {/* 5. Doctor Search */}
      <div>
        <Label>Doctor *</Label>
        <Popover open={doctorSearchOpen} onOpenChange={setDoctorSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={doctorSearchOpen}
              className={`w-full justify-between ${formErrors.selectedDoctor ? "border-red-500" : ""}`}
              data-testid="select-appointment-doctor"
              onClick={() => clearFieldError("selectedDoctor")}
            >
              {selectedDoctor
                ? `${selectedDoctor.doctor_name} - ${selectedDoctor.practice_name}`
                : "Search and select doctor..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder="Search doctors..." />
              <CommandList>
                <CommandEmpty>No doctors found.</CommandEmpty>
                <CommandGroup>
                  {doctors.map((doctor) => (
                    <CommandItem
                      key={doctor.doctor_id}
                      value={`${doctor.doctor_name} ${doctor.practice_name}`}
                      onSelect={() => {
                        setSelectedDoctor(doctor);
                        setDoctorSearchOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedDoctor?.doctor_id === doctor.doctor_id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div>
                        <p className="font-medium">{doctor.doctor_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {doctor.practice_name} • {doctor.area || doctor.city_territory || "No area"}
                        </p>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {selectedDoctor && (
          <div className="mt-2 p-3 bg-muted rounded-lg text-sm">
            <p><strong>ID:</strong> {selectedDoctor.doctor_id}</p>
            <p><strong>Practice:</strong> {selectedDoctor.practice_name}</p>
            <p><strong>Area:</strong> {selectedDoctor.area}</p>
            <p><strong>Phone:</strong> {selectedDoctor.doctor_phone}</p>
            <p><strong>Email:</strong> {selectedDoctor.doctor_email}</p>
          </div>
        )}
        {formErrors.selectedDoctor && <p className="text-xs text-red-500 mt-1">{formErrors.selectedDoctor}</p>}
      </div>

      {/* 6. Did you see the doctor? */}
      <div>
        <Label className="text-base">Did you see the doctor?</Label>
        <div className={`flex gap-3 mt-2 ${formErrors.did_see_doctor ? "border-2 border-red-500 rounded p-2" : ""}`}>
          <Button
            type="button"
            variant={appointmentForm.did_see_doctor === true ? "default" : "outline"}
            onClick={() => { setAppointmentForm({...appointmentForm, did_see_doctor: true}); clearFieldError("did_see_doctor"); }}
            className={`flex-1 ${appointmentForm.did_see_doctor === true ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            data-testid="button-appointment-did-see-doctor-yes"
          >
            Yes
          </Button>
          <Button
            type="button"
            variant={appointmentForm.did_see_doctor === false ? "default" : "outline"}
            onClick={() => { setAppointmentForm({...appointmentForm, did_see_doctor: false}); clearFieldError("did_see_doctor"); }}
            className={`flex-1 ${appointmentForm.did_see_doctor === false ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            data-testid="button-appointment-did-see-doctor-no"
          >
            No
          </Button>
        </div>
        {formErrors.did_see_doctor && <p className="text-xs text-red-500 mt-1">{formErrors.did_see_doctor}</p>}
      </div>

      {/* 7. Marketing Material Used */}
      <div>
        <Label className="flex items-center gap-1">
          Marketing Material Used *
          <span className="text-xs text-muted-foreground">(select all that apply)</span>
        </Label>
        <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-md bg-gray-50">
          {MARKETING_MATERIALS.map((material) => (
            <div key={material} className="flex items-center space-x-2">
              <Checkbox
                id={`appt-material-${material}`}
                checked={appointmentForm.marketing_material_used.includes(material)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setAppointmentForm({ ...appointmentForm, marketing_material_used: [...appointmentForm.marketing_material_used, material] });
                  } else {
                    setAppointmentForm({ ...appointmentForm, marketing_material_used: appointmentForm.marketing_material_used.filter(m => m !== material) });
                  }
                }}
                data-testid={`checkbox-appt-material-${material.replace(/\s+/g, '-').toLowerCase()}`}
              />
              <Label htmlFor={`appt-material-${material}`} className="text-sm cursor-pointer">
                {material}
              </Label>
            </div>
          ))}
        </div>
        {appointmentForm.marketing_material_used.length === 0 && (
          <p className="text-xs text-orange-600 mt-1">Please select at least one material</p>
        )}
      </div>

      {/* 8. Marketing Material Comments */}
      <div>
        <Label htmlFor="appt_marketing_comments">Marketing Material Comments</Label>
        <Textarea
          id="appt_marketing_comments"
          value={appointmentForm.marketing_material_comments}
          onChange={(e) => setAppointmentForm({ ...appointmentForm, marketing_material_comments: e.target.value })}
          placeholder="Optional notes about materials..."
          rows={2}
        />
      </div>

      {/* 9. Next Appointment Date & Time */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-4 h-4 text-blue-600" />
          <Label className="font-semibold">Next Appointment Date & Time</Label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Next Appointment Date</Label>
            <MobileDateInput
              value={nextAppointmentDate}
              onChange={(d) => {
                setNextAppointmentDate(d);
                if (d && nextAppointmentTime) {
                  checkAppointmentConflicts(format(new Date(d), "yyyy-MM-dd"), nextAppointmentTime);
                }
              }}
              placeholder="Select date"
              data-testid="select-next-appointment-date"
            />
          </div>
          <div>
            <Label htmlFor="next_appointment_time">Next Appointment Time</Label>
            <input
              type="time"
              id="next_appointment_time"
              value={nextAppointmentTime}
              onChange={(e) => {
                const newTime = e.target.value;
                setNextAppointmentTime(newTime);
                if (nextAppointmentDate && newTime) {
                  checkAppointmentConflicts(format(new Date(nextAppointmentDate), "yyyy-MM-dd"), newTime);
                }
              }}
              className="flex h-11 sm:h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              data-testid="input-next-appointment-time"
            />
            {conflictChecking && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" />
                Checking your calendar...
              </p>
            )}
            {conflictWarning && conflictWarning.length > 0 && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      Schedule conflict — {conflictWarning.length} existing appointment{conflictWarning.length > 1 ? "s" : ""} within 1 hour
                    </p>
                    <div className="mt-2 space-y-2">
                      {conflictWarning.map((conflict, i) => (
                        <div key={i} className="text-xs text-amber-700 bg-amber-100 rounded p-2">
                          <span className="font-medium">{conflict.doctor_name}</span>
                          <span className="mx-1">·</span>
                          <span>{conflict.area}</span>
                          <span className="mx-1">·</span>
                          <span>{conflict.appointment_time}</span>
                          <span className="mx-1">·</span>
                          <span>{conflict.visit_type}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-amber-600 mt-2">You can still save — this is a warning only.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          This will automatically schedule an appointment on the rep&apos;s calendar
        </p>
      </div>

      {/* 10. Gatekeeper */}
      <div>
        <Label htmlFor="appt_gatekeeper">Gatekeeper</Label>
        <Input
          id="appt_gatekeeper"
          value={appointmentForm.gatekeeper}
          onChange={(e) => setAppointmentForm({ ...appointmentForm, gatekeeper: e.target.value })}
          placeholder="Name of receptionist/secretary"
          className="h-11 sm:h-10"
        />
      </div>

      {/* 11. Comments */}
      <div>
        <Label htmlFor="appt_comments">Comments</Label>
        <Textarea
          id="appt_comments"
          value={appointmentForm.comments}
          onChange={(e) => setAppointmentForm({ ...appointmentForm, comments: e.target.value })}
          placeholder="Notes about the appointment..."
          rows={3}
        />
      </div>

      {/* 12. Requests */}
      <div>
        <Label htmlFor="appt_requests">Requests</Label>
        <Textarea
          id="appt_requests"
          value={appointmentForm.requests}
          onChange={(e) => setAppointmentForm({ ...appointmentForm, requests: e.target.value })}
          placeholder="Any requests from the doctor..."
          rows={2}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={handleDialogClose}>Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-appointment">
          {submitting ? "Saving..." : "Log Appointment"}
        </Button>
      </div>
    </form>
  );

  // Step 3D: New Doctor + Appointment = Not supported
  const renderAppointmentNewDoctorMessage = () => (
    <div className="space-y-6 py-4" data-testid="appointment-new-doctor-message">
      <div className="flex items-center gap-2 mb-4">
        <Button type="button" variant="ghost" size="sm" onClick={() => setStep(2)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Badge className="bg-blue-100 text-blue-800">Appointment</Badge>
      </div>
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          New Doctor Appointments
        </h3>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          Please add the doctor first via the Doctors page, then log an appointment.
        </p>
        <Button
          variant="outline"
          className="mt-6 gap-2"
          onClick={() => {
            setDoctorType("existing");
          }}
          data-testid="switch-to-existing-doctor"
        >
          <Users className="w-4 h-4" />
          Use Existing Doctor Instead
        </Button>
      </div>
    </div>
  );


  return (
    <div className="p-4 lg:p-6 space-y-3 sm:space-y-4 pb-6" data-testid="visits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Entries
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Log and track doctor visits &amp; appointments</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 w-full sm:w-auto h-10" data-testid="log-entry-button">
              <Plus className="w-4 h-4" />
              Log Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {step === 1 ? "Log an Entry" : step === 2 ? `Log ${entryType === "appointment" ? "Appointment" : "Visit"}` : entryType === "appointment" ? "Log Appointment" : doctorType === "existing" ? "Existing Doctor Visit" : "New Doctor Intake + Visit"}
              </DialogTitle>
            </DialogHeader>
            <AnimatePresence mode="wait">
              <motion.div
                key={step === 3 ? `3-${entryType}-${doctorType}` : `step-${step}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
                {step === 3 && entryType === "visit" && doctorType === "existing" && renderExistingDoctorForm()}
                {step === 3 && entryType === "visit" && doctorType === "new" && renderNewDoctorForm()}
                {step === 3 && entryType === "appointment" && doctorType === "existing" && renderAppointmentForm()}
                {step === 3 && entryType === "appointment" && doctorType === "new" && renderAppointmentNewDoctorMessage()}
              </motion.div>
            </AnimatePresence>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Bar */}
      <FilterBar 
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        showDoctorFilter={true}
        showRepFilter={true}
        showTerritoryFilter={true}
        showDateFilter={true}
        onPresetApply={handlePresetApply}
      />

      {/* Location Filter */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <h3 className="text-sm font-semibold mb-2">Location Filter</h3>
          <LocationSelector
            city={filterCity}
            area={filterArea}
            suburb={filterSuburb}
            onLocationChange={(location) => {
              setFilterCity(location.city);
              setFilterArea(location.area);
              setFilterSuburb(location.suburb);
            }}
            required={false}
            data-testid="visits-page-location-filter"
          />
        </CardContent>
      </Card>

      {/* Search (local filter) */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search entries by doctor, practice, or rep..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 sm:h-10"
              data-testid="search-visits"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Card>
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Entry Logs ({filteredVisits.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Rep</TableHead>
                    <TableHead className="text-xs">Doctor</TableHead>
                    <TableHead className="text-xs hidden sm:table-cell">Practice</TableHead>
                    <TableHead className="text-xs hidden sm:table-cell">Area</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Entry Type</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Saw Doctor</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Type</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Material</TableHead>
                    <TableHead className="text-right text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredVisits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 sm:py-12">
                        <ClipboardList className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground/50 mx-auto mb-3" />
                        <p className="text-muted-foreground text-sm">No entries logged</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">Log your first entry to get started</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredVisits.map((visit) => (
                      <TableRow key={visit.visit_id} data-testid={`visit-row-${visit.visit_id}`}>
                        <TableCell>{visit.date_of_visit}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getRepDisplayName(visit.rep_name)}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          <span title={formatLocation(visit.area, visit.subregion, visit.suburb)}>
                            {visit.doctor_name}
                          </span>
                          <span
                            className="block sm:hidden text-[11px] font-normal text-muted-foreground"
                            data-testid={`visit-area-${visit.visit_id}`}
                          >
                            {formatLocation(visit.area, visit.subregion, visit.suburb)}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{visit.practice_name}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge variant="outline">{formatLocation(visit.area, visit.subregion, visit.suburb)}</Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant="default" className={(visit.entry_type || "visit") === "appointment" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"} data-testid={`entry-type-badge-${visit.visit_id}`}>
                            {(visit.entry_type || "visit") === "appointment" ? "Appointment" : "Visit"}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant={visit.did_see_doctor !== false ? "default" : "secondary"} className={visit.did_see_doctor !== false ? "bg-emerald-100 text-emerald-800" : ""}>
                            {visit.did_see_doctor !== false ? "Yes" : "No"}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant="secondary">{visit.visit_type}</Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {Array.isArray(visit.marketing_material_used) 
                            ? visit.marketing_material_used.join(", ") 
                            : visit.marketing_material_used || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewVisit(visit)}
                            data-testid={`view-visit-${visit.visit_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* View Visit Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Entry Details</DialogTitle>
          </DialogHeader>
          {selectedVisit && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Entry ID</p>
                  <p className="font-mono text-sm">{selectedVisit.visit_id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p>{selectedVisit.date_of_visit}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rep</p>
                  <p>Rep {selectedVisit.rep_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Entry Type</p>
                  <Badge variant="default" className={(selectedVisit.entry_type || "visit") === "appointment" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}>
                    {(selectedVisit.entry_type || "visit") === "appointment" ? "Appointment" : "Visit"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{(selectedVisit.entry_type || "visit") === "appointment" ? "Appointment Type" : "Visit Type"}</p>
                  <p>{selectedVisit.visit_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Saw Doctor</p>
                  <Badge variant={selectedVisit.did_see_doctor !== false ? "default" : "secondary"}>
                    {selectedVisit.did_see_doctor !== false ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">Doctor Information</p>
                <div className="bg-muted p-3 rounded-lg space-y-1">
                  <p><strong>{selectedVisit.doctor_name}</strong></p>
                  <p className="text-sm">{selectedVisit.practice_name}</p>
                  <p className="text-sm">{selectedVisit.city_territory}</p>
                  <p className="text-sm">{selectedVisit.doctor_phone}</p>
                  <p className="text-sm">{selectedVisit.doctor_email}</p>
                </div>
              </div>
              {/* Area */}
              <div>
                <p className="text-sm text-muted-foreground">Area</p>
                <Badge variant="outline">{formatLocation(selectedVisit.area, selectedVisit.subregion, selectedVisit.suburb)}</Badge>
              </div>
              {selectedVisit.marketing_material_used && (
                <div>
                  <p className="text-sm text-muted-foreground">Marketing Material Used</p>
                  <p>
                    {Array.isArray(selectedVisit.marketing_material_used) 
                      ? selectedVisit.marketing_material_used.join(", ") 
                      : selectedVisit.marketing_material_used}
                  </p>
                </div>
              )}
              {selectedVisit.gifts_left && (
                <div>
                  <p className="text-sm text-muted-foreground">Marketing Material Comments</p>
                  <p>{selectedVisit.gifts_left}</p>
                </div>
              )}
              {selectedVisit.next_appointment && (
                <div>
                  <p className="text-sm text-muted-foreground">Next Appointment</p>
                  <p>{selectedVisit.next_appointment}</p>
                </div>
              )}
              {selectedVisit.gatekeeper && (
                <div>
                  <p className="text-sm text-muted-foreground">Gatekeeper</p>
                  <p>{selectedVisit.gatekeeper}</p>
                </div>
              )}
              {selectedVisit.comments && (
                <div>
                  <p className="text-sm text-muted-foreground">Comments</p>
                  <p className="text-sm">{selectedVisit.comments}</p>
                </div>
              )}
              {selectedVisit.requests && (
                <div>
                  <p className="text-sm text-muted-foreground">Requests</p>
                  <p className="text-sm">{selectedVisit.requests}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisitsPage;
